"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WixSdkAdapter = void 0;
const tslib_1 = require("tslib");
const timedOut_1 = require("../timedOut");
const win_1 = require("../win");
const sleep_1 = require("../sleep");
const guardFn = (fn, fallback = undefined, onError) => ((...args) => {
    try {
        return fn(...args);
    }
    catch (error) {
        onError === null || onError === void 0 ? void 0 : onError(error);
        return fallback;
    }
});
class WixSdkAdapter {
    constructor(params) {
        this.isTopFrame = () => guardFn(() => this.windowRef === this.windowRef.top, false)();
        this.isAvailable = guardFn(() => typeof this.windowRef !== 'undefined' &&
            (!this.isTopFrame() || this.isAllowedToRunInTopFrame) &&
            typeof this.windowRef.Wix !== 'undefined' &&
            // in BM env window.Wix is different SDK without instance re-validation support
            this.windowRef.Wix.hasOwnProperty('revalidateSession'), false);
        this.isConnected = guardFn(() => 
        // it's possible that WixSDK is available but the frame is rendered out of Viewer context (e.g. Hotels/Restaurants)
        // this means most of the WixSDK features won't be available, and callbacks will hang
        this.isAvailable() && this.getViewMode() !== 'standalone', false);
        this.isUserOfUserSignedIn = guardFn(() => Boolean(this.getMemberId()), undefined);
        this.getMemberId = guardFn(() => this.Wix.Utils.getInstanceValue('uid'));
        this.getVisitorId = guardFn(() => this.Wix.Utils.getInstanceValue('aid'));
        this.getSiteOwnerId = guardFn(() => this.Wix.Utils.getSiteOwnerId());
        this.getLocale = guardFn(() => this.Wix.Utils.getLocale());
        this.getDeviceType = guardFn(() => this.Wix.Utils.getDeviceType());
        this.getViewMode = guardFn(() => this.Wix.Utils.getViewMode());
        this.getMetasiteId = guardFn(() => this.Wix.Utils.getInstanceValue('metaSiteId') || null);
        this.getAppDefId = guardFn(() => this.Wix.Utils.getInstanceValue('appDefId') || null);
        this.getInstanceId = guardFn(() => this.Wix.Utils.getInstanceValue('instanceId') || null);
        this.openModal = (params) => {
            if (!this.isConnected() || typeof this.Wix.openModal !== 'function') {
                throw new Error(`Wix.openModal is not available`);
            }
            this.Wix.openModal(params.url, params.width, params.height, params.onClose, params.theme);
        };
        this.isAppInstanceChangeTrackable = guardFn(() => this.isConnected() && this.Wix.Events.hasOwnProperty('INSTANCE_CHANGED'));
        this.isRevalidateAppInstanceAvailable = guardFn(() => this.isConnected() && typeof this.Wix.revalidateSession === 'function');
        this.getQueryParamsFromURL = guardFn(() => {
            var _a, _b;
            const query = (_b = (_a = this.windowRef) === null || _a === void 0 ? void 0 : _a.location) === null || _b === void 0 ? void 0 : _b.search;
            if (!query) {
                return {};
            }
            return query
                .slice(1)
                .split('&')
                .reduce((params, pair) => {
                const [param, value] = pair.split('=');
                params[param] = value;
                return params;
            }, {});
        });
        this.getAppInstanceFromQuery = guardFn(() => this.getQueryParamsFromURL().instance);
        this.getDateNumberFormatFromQuery = guardFn(() => this.getQueryParamsFromURL().dateNumberFormat);
        this.getLanguageFromQuery = guardFn(() => this.getQueryParamsFromURL().lang);
        this.isPrimaryLanguageFromQuery = guardFn(() => this.getQueryParamsFromURL().isPrimaryLanguage === 'true');
        this.revalidateAppInstance = () => tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (!this.isRevalidateAppInstanceAvailable()) {
                throw new Error(`Wix.revalidateSession is not available`);
            }
            return (0, timedOut_1.timedOut)(new Promise((resolve, reject) => {
                this.Wix.revalidateSession((result) => {
                    if (typeof (result === null || result === void 0 ? void 0 : result.instance) === 'string') {
                        resolve(result.instance);
                    }
                    else {
                        reject((result === null || result === void 0 ? void 0 : result.error) || new Error('Failed to revalidate session'));
                    }
                }, reject);
            }), 5000, () => {
                throw new Error(`Failed to revalidate session, callback passed to Wix.revalidateSession wasn't called in time`);
            });
        });
        this.listenAppInstanceChange = (listener) => {
            if (!this.isAppInstanceChangeTrackable()) {
                throw new Error(`appInstance is not available for tracking via Wix SDK`);
            }
            const handleInstanceChange = (payload) => {
                listener(payload === null || payload === void 0 ? void 0 : payload.instance);
            };
            this.Wix.addEventListener(this.Wix.Events.INSTANCE_CHANGED, handleInstanceChange);
            return () => {
                if (this.isConnected()) {
                    if (typeof this.Wix.removeEventListener === 'function') {
                        this.Wix.removeEventListener(this.Wix.Events.INSTANCE_CHANGED, handleInstanceChange);
                    }
                }
            };
        };
        this.getPublicDataItem = (key) => new Promise((resolve) => {
            const getItemPromise = this.Wix.Data.Public.get(key, {
                scope: this.Wix.Data.SCOPE.COMPONENT,
            }, resolve, 
            // in case if key wasn't found WixSDK throws a rejection
            () => resolve({}));
            return Promise.race([
                getItemPromise,
                // when Public.get is run not in real site context (storybook, tests, local server) it won't ever resolve
                // thus need to limit max time waiting
                (0, sleep_1.sleep)(5000).then(() => ({})),
            ]);
        }).then((map) => map === null || map === void 0 ? void 0 : map[key]);
        this.getPublicData = (keys) => tslib_1.__awaiter(this, void 0, void 0, function* () {
            const entries = yield Promise.all(keys.map((key) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                const value = yield this.getPublicDataItem(key);
                return value !== undefined ? { key, value } : null;
            })));
            return entries.reduce((acc, entry) => {
                if (entry) {
                    acc[entry.key] = entry.value;
                }
                return acc;
            }, {});
        });
        // pass dummy cb because by default js-sdk-wrapper will provide getSiteColors,
        // getSiteTextPresets, getStyleParams that will expect a callback, while it's not really required
        // (without cb js-sdk-wrapper will throw an error 'cb is not a function')
        this.getSiteColors = guardFn(() => this.Wix.Styles.getSiteColors(() => undefined));
        this.getSiteTextPresets = guardFn(() => this.Wix.Styles.getSiteTextPresets(() => undefined));
        this.getStyleParams = guardFn(() => this.Wix.Styles.getStyleParams(() => undefined));
        this.getSiteStyles = () => {
            if (!this.isAvailable()) {
                return null;
            }
            return {
                siteColors: this.getSiteColors(),
                siteTextPresets: this.getSiteTextPresets(),
                styleParams: this.getStyleParams(),
            };
        };
        this.getPublicApi = guardFn((options, onSuccess, onFailure) => {
            return this.Wix.SuperApps.getPublicAPI(options, onSuccess, onFailure);
        });
        this.getWindowRef = params === null || params === void 0 ? void 0 : params.getWindowRef;
        this.captureError = params === null || params === void 0 ? void 0 : params.captureError;
        this.isAllowedToRunInTopFrame = !(params === null || params === void 0 ? void 0 : params.disallowTopFrame);
    }
    get windowRef() {
        return guardFn(() => { var _a; return ((_a = this.getWindowRef) === null || _a === void 0 ? void 0 : _a.call(this)) || win_1.win; }, win_1.win)();
    }
    get Wix() {
        return guardFn(() => (this.isAvailable() && this.windowRef.Wix) || undefined, undefined)();
    }
}
exports.WixSdkAdapter = WixSdkAdapter;
//# sourceMappingURL=index.js.map